import { useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { useMe, useOrg } from "@/hooks/data";
import { useApiQuery } from "@/lib/api/hooks";
import { queryClient } from "@/lib/api/query-client";
import type { CalendarCountry } from "@/lib/types";

/** Every calendar read route (for cache invalidation after saves and live events). */
export const CALENDAR_ROUTES = ["calendarEvents", "calendarAlerts", "calendarConflicts", "calendarSubscription", "previewCalendarSubscription", "calendarSourcesHealth"] as const;

export function invalidateCalendar(qc = queryClient) {
  return Promise.all(CALENDAR_ROUTES.map((r) => qc.invalidateQueries({ queryKey: [r] })));
}

export function useInvalidateCalendar() {
  const qc = useQueryClient();
  return () => invalidateCalendar(qc);
}

export function useCalendarEvents(range: { from: string; to: string }, accountId: string | null) {
  return useApiQuery("calendarEvents", { query: { from: range.from, to: range.to, ...(accountId ? { accountId } : {}) } }, { staleTime: 5 * 60_000 });
}

export function useCalendarAlerts(accountId: string | null = null) {
  return useApiQuery("calendarAlerts", { query: accountId ? { accountId } : {} }, { staleTime: 60_000, refetchInterval: 5 * 60_000 });
}

export function useCalendarConflicts(range: { from: string; to: string }, accountId: string | null) {
  return useApiQuery("calendarConflicts", { query: { from: range.from, to: range.to, ...(accountId ? { accountId } : {}) } }, { staleTime: 60_000 });
}

export function useCatalog() {
  const q = useApiQuery("calendarCatalog", {}, { staleTime: Infinity });
  const byCode = useMemo(() => new Map<string, CalendarCountry>((q.data?.countries ?? []).map((c) => [c.code, c])), [q.data]);
  return { ...q, byCode };
}

/** Owners and admins may approve crises, refresh sources. */
export function useCanAdmin() {
  const org = useOrg();
  return org?.role === "owner" || org?.role === "admin";
}

export function useCanEdit() {
  const org = useOrg();
  return !!org && org.role !== "viewer";
}

export function useMyUserId() {
  return useMe().data?.user.id ?? null;
}
